import { Feature, FeatureCollection } from 'geojson';
import mapboxgl, { GeoJSONSource } from 'mapbox-gl';
import 'mapboxgl-legend/dist/style.css';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { IChosenBoatObject } from '../../pages/Report/interactiveReport/PathfinderReport';
import { ReportType } from '../../utils/enums';
import { PathfinderData } from '../../utils/interfaces';
import { addGapPopup, addMouseClickAisGapEffect, addMouseMoveHoverEffect } from '../../utils/map';
import constants from '../constants';
import { AddDougaMapLayers } from './DougaCaseUtils';
import { addFillLayer, addPopup, highlightFeature, ILayer } from './MapComponents';

const PathfinderMap: React.FC<{
    containerRef: React.RefObject<HTMLDivElement>;
    pathfinderData: PathfinderData | undefined;
    pathfinderAisData: FeatureCollection | undefined;
    boatMetaData: Feature | null;
    setChosenBoatObject: (value: IChosenBoatObject | null) => void;
    hoveredPathElement: number | null;
    filterStartDate: number;
    filterEndDate: number;
}> = ({
    containerRef,
    pathfinderData,
    pathfinderAisData,
    boatMetaData,
    setChosenBoatObject,
    hoveredPathElement,
    filterStartDate,
    filterEndDate,
}) => {
    const [currMap, setMap] = useState<undefined | mapboxgl.Map>(undefined);
    const popupRef = useRef(new mapboxgl.Popup({ offset: 15 }));
    const gapsPopupsRef = useRef(new mapboxgl.Popup({ offset: 20 }));
    const params = useParams();
    const isDougaCase: boolean = params.id?.split('_')[0] === '352002013';
    useEffect(() => {
        if (containerRef.current && !currMap) {
            const coords = {
                lat: 57.33870347771735,
                lng: 2.042840975438542,
            };
            const zmm = 4;
            mapboxgl.accessToken =
                'pk.eyJ1IjoidmFrZS10b3JzdGVpbiIsImEiOiJjbDR4emJmbW0xdTRpM21tbG1lbnc3b2JrIn0.V7PXI4I2ndH2mxaJrVCnRw';
            const maps: mapboxgl.Map = new mapboxgl.Map({
                container: containerRef.current,
                center: coords,
                zoom: zmm,
                style: 'mapbox://styles/vake-torstein/cl4xzn1gs000p14qgz8nqs0ms',
            });

            //Broadcast loaded
            maps.on('load', () => {
                maps.loadImage('/images/icons/ArrowRight.png', (error, image) => {
                    if (error) {
                        throw error;
                    }
                    maps.addImage('arrow_right', image);
                });
                setMap(maps);
                maps.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
                maps.addControl(new mapboxgl.ScaleControl({ maxWidth: 500 }));
            });
        }
    }, [currMap, containerRef, params]);

    // useEffect for adding layers to map
    useEffect(() => {
        const setHiglightedFeatureVisibility = (visible: boolean) => {
            if (currMap) {
                setLayerVisibility(currMap, visible, 'selectedCircle');
            }
        };
        if (currMap && pathfinderData) {
            if (pathfinderData.cloudmasks) addCloudmasksLayer(currMap, pathfinderData);
            if (pathfinderData.possibility_area_in_gap_at_datatake_sensing_time)
                addPossibilityAreaInGapAtSensingTimeLayer(currMap, pathfinderData);
            if (pathfinderData.oil_pipelines) addOilPipelinesLayer(currMap, pathfinderData);
            if (pathfinderAisData) {
                addAISMessages(currMap, pathfinderAisData);
            }
            if (pathfinderData.ais_tracks) addAisTracksLayer(currMap, pathfinderData, filterStartDate);
            if (pathfinderData.ais_gaps) addAisGapsLayer(currMap, pathfinderData, gapsPopupsRef, popupRef);
            if (pathfinderData.ais_gaps_with_images) addAisGapsWithImagesLayer(currMap, pathfinderData, popupRef);
            addMapLayers(currMap, pathfinderData, popupRef, setHiglightedFeatureVisibility, setChosenBoatObject);
            if (isDougaCase && !currMap.getLayer('douga_black_sea_marker')) {
                AddDougaMapLayers(currMap);
            }
        }

        // Fly to first satellite detection if avaliable, else fly to first ais message
        if (currMap && pathfinderData?.ais_positions?.features?.[0]?.geometry?.type === 'Point') {
            const endCoordinates = pathfinderData.ais_positions.features[0].geometry.coordinates;
            if (Array.isArray(endCoordinates) && endCoordinates.length >= 2) {
                currMap.flyTo({
                    center: { lng: endCoordinates[0], lat: endCoordinates[1] },
                    pitch: 0,
                    bearing: 0,
                    duration: 2022,
                    essential: true,
                });
            }
        } else if (currMap && pathfinderData?.ais_tracks?.features?.[0]?.geometry?.type === 'LineString') {
            const endCoordinates = pathfinderData.ais_tracks.features[0].geometry.coordinates;
            if (Array.isArray(endCoordinates) && endCoordinates[0]?.length >= 2) {
                currMap.flyTo({
                    center: { lng: endCoordinates[0][0], lat: endCoordinates[0][1] },
                    pitch: 0,
                    bearing: 0,
                    duration: 2022,
                    essential: true,
                });
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currMap, pathfinderData, pathfinderAisData]);

    useEffect(() => {
        if (!currMap || !pathfinderData) return;

        const timeRangeFilter = [
            'all',
            ['>=', ['get', 'start_time_epoch'], filterStartDate],
            ['<=', ['get', 'end_time_epoch'], filterEndDate],
        ];

        // Apply filters for ais_tracks_pathfinder
        if (
            currMap.getLayer('ais_tracks_pathfinder') &&
            pathfinderData.ais_tracks?.features?.[0]?.properties?.end_time_epoch &&
            pathfinderData.ais_tracks.features[0].properties.start_time_epoch
        ) {
            currMap.setFilter('ais_tracks_pathfinder', timeRangeFilter);
            currMap.setFilter('ais_tracks_pathfinder_hover', timeRangeFilter);
            currMap.setFilter('ais_track_arrows_pathfinder', timeRangeFilter);
        }

        // Apply filters for ais_gaps_pathfinder
        if (
            currMap.getLayer('ais_gaps_pathfinder') &&
            pathfinderData.ais_gaps?.features?.[0]?.properties?.end_time_epoch &&
            pathfinderData.ais_gaps.features[0].properties?.start_time_epoch
        ) {
            currMap.setFilter('ais_gaps_pathfinder', timeRangeFilter);
            currMap.setFilter('ais_gaps_pathfinder_hover', timeRangeFilter);
        }

        // Apply filters for ais_gaps_with_images_pathfinder
        if (
            currMap.getLayer('ais_gaps_with_images_pathfinder') &&
            pathfinderData.ais_gaps_with_images?.features?.[0]?.properties?.end_time_epoch &&
            pathfinderData.ais_gaps_with_images.features[0].properties?.start_time_epoch
        ) {
            currMap.setFilter('ais_gaps_with_images_pathfinder', timeRangeFilter);
            currMap.setFilter('ais_gaps_with_images_pathfinder_hover', timeRangeFilter);
        }

        // Update source for estimated_ais_pathfinder
        if (currMap.getSource('estimated_ais_pathfinder') && pathfinderData?.ais_positions?.features?.[0]) {
            const newAisPositions: FeatureCollection = { type: 'FeatureCollection', features: [] };
            const features = pathfinderData.ais_positions.features.filter(
                (image) =>
                    image.properties?.timestamp_epoch > filterStartDate &&
                    image.properties?.timestamp_epoch < filterEndDate
            );
            newAisPositions.features = features ? features : [];
            (currMap.getSource('estimated_ais_pathfinder') as GeoJSONSource).setData(newAisPositions); // Filter this way because setFilter will warn of undefined in clustering calculation in the other way
        }

        // Update filters for ais_messages_pathfinder
        if (currMap.getSource('ais_messages_pathfinder') && pathfinderData.ais_tracks?.features) {
            // NB! This is a hack until we switch to Deck-gl and can use TripsLayer

            // Variable to keep track of the feature with the highest value
            let maxValue = filterStartDate; // arbitrary values, is guaranteed to be overridden.
            let minValue = filterEndDate;

            // Iterate through the features to find the one with the highest value
            pathfinderData.ais_tracks.features.forEach((feature) => {
                const startTimestamp = feature.properties?.start_time_epoch;
                const endTimestamp = feature.properties?.end_time_epoch;

                // Ensure the property exists and is a number
                if (endTimestamp !== undefined && endTimestamp >= maxValue && endTimestamp <= filterEndDate) {
                    maxValue = endTimestamp;
                }
                if (startTimestamp !== undefined && startTimestamp <= minValue && startTimestamp >= filterStartDate) {
                    minValue = startTimestamp;
                }
            });
            const timeRangeFilterAISMessages = [
                'any',
                ['all', ['>=', ['get', 'dt_pos_utc'], filterStartDate], ['<=', ['get', 'dt_pos_utc'], minValue]],
                ['all', ['>=', ['get', 'dt_pos_utc'], maxValue], ['<=', ['get', 'dt_pos_utc'], filterEndDate]],
            ];
            currMap.setFilter('ais_messages_pathfinder', timeRangeFilterAISMessages);
            currMap.setLayoutProperty('ais_messages_pathfinder', 'visibility', 'visible');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterStartDate, filterEndDate]);

    useEffect(() => {
        if (boatMetaData?.geometry?.type === 'Point') {
            const coordinates = boatMetaData.geometry.coordinates;

            // Ensure coordinates array has at least two valid numbers
            if (Array.isArray(coordinates) && coordinates.length >= 2) {
                const [lon, lat] = coordinates;
                if (typeof lon === 'number' && typeof lat === 'number') {
                    currMap?.flyTo({ center: { lon, lat }, duration: 2700 });
                }
            }
        }
    }, [currMap, boatMetaData]);

    useEffect(() => {
        if (typeof hoveredPathElement === 'number') {
            const candidate = pathfinderData?.ais_positions?.features?.[hoveredPathElement];
            if (candidate && currMap) {
                // Validate that candidate has valid geometry and properties
                if (candidate.geometry && candidate.properties) {
                    const tempFeature: mapboxgl.MapboxGeoJSONFeature = {
                        layer: { id: 'selectedCircle', type: 'circle' },
                        source: 'selectedCircle',
                        sourceLayer: 'selectedCircle',
                        state: { hover: true },
                        geometry: candidate.geometry,
                        properties: candidate.properties,
                        type: candidate.type,
                    };

                    // Highlight the feature
                    highlightFeature(currMap, tempFeature, 'orange', (_: boolean) => true);
                }
            }
        } else {
            if (currMap?.getLayer('selectedCircle')) {
                currMap.removeLayer('selectedCircle');
            }
            if (currMap?.getSource('selectedCircle')) {
                currMap.removeSource('selectedCircle');
            }
        }
    }, [currMap, hoveredPathElement, pathfinderData]);

    return <div className="fixed w-screen h-screen" ref={containerRef} />;
};

export default PathfinderMap;

const hasGapIx = (features: Feature[]): boolean => {
    return features.length > 0 && features[0].properties !== null && 'gap_ix' in features[0].properties;
};

const addMapLayers = (
    currMap: mapboxgl.Map,
    insights: PathfinderData,
    popupRef: React.MutableRefObject<mapboxgl.Popup>,
    setHiglightedFeatureVisibility: (visible: boolean) => void,
    setChosenBoatObject: (value: IChosenBoatObject | null) => void
) => {
    const insight_id = insights?.id;
    if (!insight_id) {
        return;
    }

    const colors = constants.colors.markers;
    const layers: ILayer[] = [
        {
            id: `undetected_ais_${insight_id}`,
            name: 'undetected_ais',
            date: insight_id,
            data: insights?.undetected_ais,
            fill: colors.undetectedAis.fill,
            filter:
                insights?.undetected_ais && hasGapIx(insights.undetected_ais.features)
                    ? ['==', 'gap_ix', '']
                    : undefined,
            stroke: colors.undetectedAis.stroke,
            type: 'circle',
            popupType: 'detection',
            circleRadius: 3,
            cluster: false,
        },
        {
            id: `dark_vessels_${insight_id}`,
            name: 'dark_vessels',
            date: insight_id,
            data: insights?.dark_vessels,
            fill: colors.darkShips.fill,
            filter:
                insights?.undetected_ais && hasGapIx(insights.undetected_ais.features)
                    ? ['==', 'gap_ix', '']
                    : undefined,
            stroke: colors.darkShips.stroke,
            type: 'circle',
            popupType: 'detection',
            cluster: false,
        },
        {
            id: `matched_vessels_${insight_id}`,
            name: 'matched_vessels',
            date: insight_id,
            data: insights?.matched_vessels,
            fill: colors.matchedVessels.fill,
            filter:
                insights?.undetected_ais && hasGapIx(insights.undetected_ais.features)
                    ? ['==', 'gap_ix', '']
                    : undefined,
            stroke: colors.matchedVessels.stroke,
            type: 'circle',
            popupType: 'detection',
            cluster: false,
        },
        {
            id: `estimated_ais_${insight_id}`,
            name: 'estimated_ais',
            date: insight_id,
            data: insights?.ais_positions,
            fill: [
                'match',
                ['get', 'image_source'],
                'sentinel1',
                'grey',
                'sentinel2',
                '#f0d971',
                'planet_scope',
                '#00A1FF',
                '#8de',
            ],
            stroke: '#000',
            type: 'circle',
            popupType: undefined,
            cluster: true,
        },
        {
            id: `static_objects_${insight_id}`,
            name: 'static_objects',
            date: insight_id,
            data: insights?.static_objects ?? null, // FeatureCollection expects null instead of undefined
            fill: '#294868',
            stroke: '#000',
            type: 'circle',
            popupType: 'toi',
            cluster: false,
        },
        {
            id: `images_of_gaps_${insight_id}`,
            name: 'images_of_gaps',
            date: insight_id,
            static: true,
            data: insights?.images_of_gaps ?? null, // FeatureCollection expects null instead of undefined
            fill: [
                'match',
                ['get', 'image_source'],
                'sentinel1',
                'grey',
                'sentinel2',
                '#f0d971',
                /* other */ '#00F5FF',
            ],
            filter: ['==', 'gap_ix', ''],
            fillOpacity: 0.3,
            type: 'fill',
            cluster: false,
        },
    ];
    layers.forEach((layer) => {
        if (layer.data) {
            // Only process layers with valid data
            if (layer.type === 'circle') {
                addCircleLayer(
                    currMap,
                    layer.data,
                    layer.id,
                    layer.fill,
                    layer.filter,
                    layer.stroke!,
                    popupRef,
                    layer.circleRadius!,
                    setHiglightedFeatureVisibility,
                    setChosenBoatObject,
                    layer.cluster,
                    layer.popupType
                );
            } else if (layer.type === 'fill') {
                addFillLayer(currMap, layer.data, layer.id, layer.fill, layer.fillOpacity!, layer.filter!);
            }
        }
    });
};

const addCircleLayer = (
    currMap: mapboxgl.Map,
    data: FeatureCollection,
    id: string,
    fill: string,
    filter: string[] | undefined,
    stroke: string,
    popupRef: React.MutableRefObject<mapboxgl.Popup>,
    circleRadius: number = 4,
    setHiglightedFeatureVisibility: (visible: boolean) => void,
    setChosenBoatObject: (value: IChosenBoatObject | null) => void,
    cluster?: boolean,
    popupType?: 'detection' | 'toi',
    staticObj?: boolean
) => {
    if (currMap && !currMap.getSource(id)) {
        const point_hover = ['all', ['==', ['feature-state', 'hover'], true]];
        // debugger;
        const hoverId = `${id}_hover`;
        const layerDict: mapboxgl.CircleLayer = {
            id: id,
            type: 'circle',
            source: id,
            layout: {
                visibility: 'visible',
            },
            paint: {
                'circle-radius': cluster
                    ? ['step', ['get', 'point_count'], circleRadius, 1, circleRadius * 2, 7, circleRadius * 2.75]
                    : circleRadius,
                'circle-color': fill,
                'circle-stroke-color': ['case', point_hover, 'white', stroke],
                'circle-stroke-width': 1,
                'circle-opacity': 1,
            },
        };
        if (filter) {
            layerDict['filter'] = filter;
        }
        currMap.addSource(id, {
            type: 'geojson',
            data: data,
            cluster: cluster ? true : false,
            clusterMaxZoom: 10,
            clusterRadius: 12,
        });
        if (cluster) {
            currMap.addLayer({
                id: 'clusters',
                type: 'circle',
                source: id,
                filter: ['has', 'point_count'],
                layout: {
                    visibility: 'visible',
                },
                paint: {
                    'circle-radius': [
                        'step',
                        ['get', 'point_count'],
                        circleRadius,
                        1,
                        circleRadius * 2,
                        6,
                        circleRadius * 2.75,
                    ],
                    'circle-color': ['step', ['get', 'point_count'], '#f0d971', 1, '#fad060', 6, '#ffC060'],
                    'circle-stroke-color': ['case', point_hover, 'white', stroke],
                    'circle-stroke-width': 1,
                    'circle-opacity': 1,
                },
            });
            layerDict['filter'] = ['!', ['has', 'point_count']];
            currMap.addLayer(layerDict);
            currMap.addLayer({
                id: 'point-count',
                type: 'symbol',
                source: id,
                filter: ['has', 'point_count'],
                layout: {
                    'text-field': ['get', 'point_count_abbreviated'],
                    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                    'text-size': 12,
                },
            });
            currMap.on('click', 'clusters', (e) => {
                const features = currMap.queryRenderedFeatures(e.point, {
                    layers: ['clusters'],
                });
                const clusterId = features[0].properties?.cluster_id;
                const source: mapboxgl.GeoJSONSource = currMap.getSource(id) as mapboxgl.GeoJSONSource;
                source.getClusterExpansionZoom(clusterId, (err, zoom) => {
                    if (err) return;
                    if (features[0].geometry.type === 'Point') {
                        currMap.easeTo({
                            center: {
                                lon: features[0].geometry?.coordinates[0],
                                lat: features[0].geometry?.coordinates[1],
                            },
                            zoom: zoom,
                            duration: 1300,
                        });
                    }
                });
            });
        } else {
            currMap.addLayer(layerDict);
        }
        if (!staticObj) {
            currMap.addSource(hoverId, {
                type: 'geojson',
                data: data,
                generateId: true, // This ensures that all features have unique IDs
            });
            const hoverLayerObj: mapboxgl.CircleLayer = {
                id: hoverId,
                type: 'circle',
                source: hoverId,
                layout: {
                    visibility: 'visible',
                },
                paint: {
                    'circle-radius': ['case', ['boolean', ['feature-state', 'hover'], false], 12, 3],
                    'circle-stroke-color': fill,
                    'circle-stroke-width': 2,
                    'circle-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.2, 0],
                    'circle-stroke-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.4, 0],
                    'circle-color': fill,
                },
                metadata: {
                    name: 'Satellites',
                },
            };
            if (filter) {
                hoverLayerObj['filter'] = filter;
            }
            currMap.addLayer(hoverLayerObj);

            let hoveredItem: string | number | undefined = undefined;

            currMap.on('mousemove', hoverId, ({ features }) => {
                currMap.getCanvas().style.cursor = 'pointer';

                if (!features) return;
                if (features.length === 0) return;

                if (hoveredItem) {
                    currMap.removeFeatureState({
                        source: hoverId,
                        id: hoveredItem,
                    });
                }

                hoveredItem = features[0].id;

                currMap.setFeatureState(
                    {
                        source: hoverId,
                        id: hoveredItem ? hoveredItem : 'hei',
                    },
                    {
                        hover: true,
                    }
                );
            });
            currMap.on('click', function (e) {
                const features = currMap.queryRenderedFeatures(e.point, { layers: [id] });
                if (!features.length) {
                    return;
                }
                const feature = features[0];
                if (feature.properties) {
                    const chosen: IChosenBoatObject = {
                        lat: feature.properties.lat,
                        lon: feature.properties.lon,
                        timestamp: feature.properties.timestamp,
                        image_path: feature.properties.img_path,
                        id: feature.id,
                    };
                    setChosenBoatObject(chosen);
                }
                highlightFeature(currMap, feature, fill, setHiglightedFeatureVisibility);
            });
            currMap.on('mouseleave', hoverId, () => {
                if (hoveredItem) {
                    currMap.setFeatureState(
                        {
                            source: hoverId,
                            id: hoveredItem,
                        },
                        {
                            hover: false,
                        }
                    );
                }
                hoveredItem = undefined;
                // Remove the information from the previously hovered feature from the sidebar

                // Reset the cursor style
                currMap.getCanvas().style.cursor = '';
            });
        }
        if (popupType)
            addPopup(currMap, id, popupRef, ReportType.PathfinderReport, popupType, setHiglightedFeatureVisibility);
    }
};

const addAISMessages = (currMap: mapboxgl.Map, item: FeatureCollection) => {
    const ais_messages_id = `ais_messages_pathfinder`;
    if (currMap && !currMap.getSource(ais_messages_id)) {
        currMap
            .addSource(ais_messages_id, {
                type: 'geojson',
                data: item,
            })
            .addLayer({
                id: ais_messages_id,
                type: 'circle',
                source: ais_messages_id,
                layout: {
                    // make layer visible by default
                    visibility: 'none',
                },
                paint: {
                    'circle-radius': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        10, // Zoom level 10
                        3, // Radius at zoom 10
                        15, // Zoom level 15
                        1, // Radius at zoom 15
                    ],
                    'circle-color': '#722fb1',
                },
            });
    }
};

const addAisTracksLayer = (currMap: mapboxgl.Map, item: PathfinderData, filterStartDate: number) => {
    const ais_tracks_id = `ais_tracks_${item.id}`;
    const ais_tracks_id_hover = ais_tracks_id + '_hover';
    if (currMap && !currMap.getSource(ais_tracks_id)) {
        currMap
            .addSource(ais_tracks_id, {
                type: 'geojson',
                data: item.ais_tracks,
            })
            .addLayer({
                // This layer is for listening to hovers, needs to have same name as source
                id: ais_tracks_id,
                type: 'line',
                source: ais_tracks_id,
                layout: {
                    visibility: 'visible',
                },
                paint: {
                    'line-width': 20,
                    'line-opacity': 0,
                },
            })
            .addLayer({
                id: ais_tracks_id_hover,
                type: 'line',
                // filter: ['>=','start_timestamp',  ]
                source: ais_tracks_id,
                layout: {
                    // make layer visible by default
                    visibility: 'visible',
                },
                paint: {
                    'line-width': [
                        'case',
                        ['==', ['feature-state', 'hover'], true],
                        2, // when hover
                        1, // when normal
                    ],
                    'line-color': [
                        'case',
                        ['==', ['feature-state', 'hover'], true],
                        '#722fb1', // when hover
                        '#9671b8', // when normal
                    ],
                },
            })
            .addLayer({
                id: `ais_track_arrows_${item.id}`,
                type: 'symbol',
                source: ais_tracks_id,
                layout: {
                    'symbol-placement': 'line',
                    'icon-image': 'arrow_right',
                    'icon-size': 0.5,
                },
            });
        addMouseMoveHoverEffect(currMap, ais_tracks_id);
    }
};

const addAisGapsLayer = (
    currMap: mapboxgl.Map,
    item: PathfinderData,
    gapsPopupsRef: React.MutableRefObject<mapboxgl.Popup>,
    popupRef: React.MutableRefObject<mapboxgl.Popup>
) => {
    const ais_gaps_id = `ais_gaps_${item.id}`;
    const ais_gaps_id_hover = ais_gaps_id + '_hover';
    if (currMap && !currMap.getSource(ais_gaps_id)) {
        currMap
            .addSource(ais_gaps_id, {
                type: 'geojson',
                data: item.ais_gaps,
            })
            .addLayer({
                // This layer is for listening to hovers, needs to have same name as source
                id: ais_gaps_id,
                type: 'line',
                source: ais_gaps_id,
                layout: {
                    visibility: 'visible',
                },
                paint: {
                    'line-width': 15,
                    'line-opacity': 0,
                },
            })
            .addLayer({
                id: ais_gaps_id_hover,
                type: 'line',
                source: ais_gaps_id,
                layout: {
                    // make layer visible by default
                    visibility: 'visible',
                },
                paint: {
                    'line-width': 3,
                    'line-color': [
                        'case',
                        ['boolean', ['feature-state', 'hover'], true],
                        '#e79325', // when hover e9a54b
                        '#e7a86c', // when normal
                    ],
                    'line-dasharray': [2, 2],
                },
            });

        addMouseMoveHoverEffect(currMap, ais_gaps_id, true, gapsPopupsRef);
        addGapPopup(currMap, ais_gaps_id, popupRef);
    }
};

const addAisGapsWithImagesLayer = (
    currMap: mapboxgl.Map,
    item: PathfinderData,
    popupRef: React.MutableRefObject<mapboxgl.Popup>
) => {
    const ais_gaps_with_images_id = `ais_gaps_with_images_${item.id}`;
    const ais_gaps_with_images_id_hover = ais_gaps_with_images_id + '_hover';
    if (currMap && !currMap.getSource(ais_gaps_with_images_id)) {
        currMap
            .addSource(ais_gaps_with_images_id, {
                type: 'geojson',
                data: item.ais_gaps_with_images,
            })
            .addLayer({
                // This layer is for listening to hovers, needs to have same name as source
                id: ais_gaps_with_images_id,
                type: 'line',
                source: ais_gaps_with_images_id,
                layout: {
                    visibility: 'visible',
                },
                paint: {
                    'line-width': 30,
                    'line-opacity': 0,
                },
            })
            .addLayer({
                id: ais_gaps_with_images_id_hover,
                type: 'line',
                source: ais_gaps_with_images_id,
                layout: {
                    // make layer visible by default
                    visibility: 'visible',
                },
                paint: {
                    'line-width': 3,
                    'line-color': [
                        'case',
                        [
                            'all',
                            ['==', ['feature-state', 'itIsClicked'], true],
                            ['==', ['feature-state', 'hover'], true],
                        ], // layer hovered and clicked
                        '#e72d2d',
                        ['==', ['feature-state', 'hover'], true], // layer hover
                        '#e44a4a',
                        ['==', ['feature-state', 'itIsClicked'], true], // layer clicked,
                        '#e72d2d',
                        '#ec8e8e', // default
                    ],
                    'line-dasharray': [2, 2],
                },
            });

        addMouseMoveHoverEffect(currMap, ais_gaps_with_images_id);
        addMouseClickAisGapEffect(currMap, ais_gaps_with_images_id, [
            'undetected_ais_pathfinder',
            'images_of_gaps_pathfinder',
            'dark_vessels_pathfinder',
            'matched_vessels_pathfinder',
            'cloudmasks_pathfinder',
            'images_of_gaps_pathfinder',
            'possibility_area_in_gap_at_datatake_sensing_time_pathfinder',
        ]);
        addGapPopup(currMap, ais_gaps_with_images_id, popupRef, item.possibility_area_in_gap_at_datatake_sensing_time);
    }
};

const addOilPipelinesLayer = (currMap: mapboxgl.Map, item: PathfinderData) => {
    const oil_pipelines_id = `oil_pipelines_${item.id}`;
    const oil_pipelines_id_hover = oil_pipelines_id + '_hover';
    if (currMap && !currMap.getSource(oil_pipelines_id)) {
        currMap
            .addSource(oil_pipelines_id, {
                type: 'geojson',
                data: item.oil_pipelines,
            })
            .addLayer({
                // This layer is for listening to hovers, needs to have same name as source
                id: oil_pipelines_id,
                type: 'line',
                source: oil_pipelines_id,
                layout: {
                    visibility: 'visible',
                },
                paint: {
                    'line-width': 30,
                    'line-opacity': 0,
                },
            })
            .addLayer({
                // This layer is for looking hovered when layer above is hovered
                id: oil_pipelines_id_hover,
                type: 'line',
                source: oil_pipelines_id,
                layout: {
                    visibility: 'visible',
                },
                paint: {
                    'line-width': [
                        'case',
                        ['==', ['feature-state', 'hover'], true],
                        2, // when hover
                        1, // when normal
                    ],
                    'line-color': [
                        'case',
                        ['==', ['feature-state', 'hover'], true],
                        '#aaa', //'#4d545e', // when hover 6b97dd
                        '#777777', //'#343435', // when normal
                    ],
                },
            });
        addMouseMoveHoverEffect(currMap, oil_pipelines_id);
    }
};

const addCloudmasksLayer = (currMap: mapboxgl.Map, item: PathfinderData) => {
    const cloudmasks_id = `cloudmasks_${item.id}`;
    if (currMap && !currMap.getSource(cloudmasks_id)) {
        currMap
            .addSource(cloudmasks_id, {
                type: 'geojson',
                data: item.cloudmasks,
            })
            .addLayer({
                id: cloudmasks_id,
                type: 'fill',
                source: cloudmasks_id,
                layout: {
                    visibility: 'visible',
                },
                paint: {
                    'fill-color': '#c0e0f3',
                    'fill-opacity': 0.2,
                },
                filter: ['==', 'gap_ix', ''],
            });
    }
};

const addPossibilityAreaInGapAtSensingTimeLayer = (currMap: mapboxgl.Map, item: PathfinderData) => {
    const layer_id = `possibility_area_in_gap_at_datatake_sensing_time_${item.id}`;
    if (currMap && !currMap.getSource(layer_id)) {
        currMap
            .addSource(layer_id, {
                type: 'geojson',
                data: item.possibility_area_in_gap_at_datatake_sensing_time,
            })
            .addLayer({
                id: layer_id,
                type: 'fill',
                source: layer_id,
                layout: {
                    visibility: 'visible',
                },
                paint: {
                    'fill-color': '#a762a8',
                    'fill-opacity': 0.5,
                },
                filter: ['==', 'gap_ix', ''],
            });
    }
};

const setLayerVisibility = (currMap: mapboxgl.Map, visible: boolean, layer_id: string) => {
    const visibility = visible ? 'visible' : 'none';
    if (layerExists(currMap, layer_id)) {
        currMap.setLayoutProperty(layer_id, 'visibility', visibility);
        if (layerExists(currMap, `${layer_id}_hover`)) {
            currMap.setLayoutProperty(`${layer_id}_hover`, 'visibility', visibility);
        }
    }
};

export const layerExists = (currMap: mapboxgl.Map, layer: string) => {
    return !!currMap.getLayer(layer);
};
